<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { toast } from "vue-sonner";
import AppPopover from "../components/AppPopover.vue";
import FormField from "../components/FormField.vue";
import LucideIcon from "../components/icons/LucideIcon.vue";
import StatusBanner from "../components/StatusBanner.vue";
import Textbox from "../components/Textbox.vue";
import {
  mapIntermediateOrderRowsToLookupResult,
  matchesIntermediateOrder,
} from "../features/bulkOrderCreation/bulkOrderCreationLiveLookup.ts";
import {
  isMockEmptyIdentifier,
  isMockInvalidIdentifier,
  lookupBulkOrderCreation,
  type AssociatedIntermediateOrder,
  type BulkOrderCreationRow,
} from "../features/bulkOrderCreation/bulkOrderCreationMock.ts";
import {
  createAlgorithmDemoRows,
  DEMO_ALGORITHM_WAREHOUSES,
  demoAssociatedOrders,
  demoRowsForIntermediateOrder,
  isDemoAlgorithmIdentifier,
  resolveDemoIntermediateOrderId,
} from "../features/bulkOrderCreation/demoShippingScenario.ts";
import FullIntermediateOrderModal from "../features/bulkOrderCreation/FullIntermediateOrderModal.vue";
import ShippingPoGenerationWorkspace from "../features/bulkOrderCreation/ShippingPoGenerationWorkspace.vue";
import type { ShippingPoWorkspaceControls } from "../features/bulkOrderCreation/shippingPoSettings.ts";
import {
  buildSuggestedOrdersDemoShippingRows,
  SUGGESTED_ORDERS_DEMO_ROW_COUNT,
  type SuggestedOrdersDemoRow,
} from "../features/bulkOrderCreation/suggestedOrdersDemo.ts";
import SuggestedOrdersDemo from "../features/bulkOrderCreation/SuggestedOrdersDemo.vue";
import { classifyBulkOrIntermediateOrderIdentifier } from "../features/home/bulkIntermediateOrderSearch.ts";
import BulkOrderAllocationReview from "../features/intermediateOrders/BulkOrderAllocationReview.vue";
import type { DropdownOption } from "../redistribution/models/DropdownOption.ts";
import type {
  BulkOrderAllocationRow,
  IntermediateOrderSearchDiagnostics,
} from "../redistribution/models/IntermediateOrderSearchResult.ts";
import { searchIntermediateOrderResultByIdentifier } from "../services/intermediateOrderService.ts";
import { fetchWarehouses } from "../services/purchaseOrderService.ts";
import { useModalStore } from "../stores/modalStore.ts";
import { isDemoModeEnabled } from "../utils/demoMode.ts";

const LOOKUP_SIMULATED_DELAY_MS = 1800;
function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const modalStore = useModalStore();
const route = useRoute();
const isDemoMode = computed(() => isDemoModeEnabled(route.query));
const fullOrderTrigger = ref<HTMLButtonElement>();
let demoToastId: ReturnType<typeof toast.info> | undefined;
onMounted(() => {
  if (isDemoMode.value) {
    demoToastId = toast.info(
      "Demo mode is on: identifier searches use sample order data and Suggested Orders shows the randomized Orders and Details export views.",
      { duration: 5000 }
    );
  }
});
onUnmounted(() => {
  if (demoToastId !== undefined) toast.dismiss(demoToastId);
});
watch(isDemoMode, (enabled) => {
  if (!enabled && demoToastId !== undefined) toast.dismiss(demoToastId);
});
watch(
  () => modalStore.isOpen("bulk-order-creation-full-io"),
  async (open, wasOpen) => {
    if (!open && wasOpen) {
      await nextTick();
      fullOrderTrigger.value?.focus();
    }
  }
);
const demoModeToggleQuery = computed(() => ({
  ...route.query,
  demo: isDemoMode.value ? "0" : "1",
}));
const warehouses = ref<DropdownOption[]>([]);
const identifier = ref("");
const generationIdentifier = ref("");
const associatedGenerationError = ref("");
const associatedIntermediateOrderIdentifier = ref("");
const detectedMode = computed(() => {
  if (identifier.value.trim() === associatedIntermediateOrderIdentifier.value) {
    return "intermediate-order";
  }
  return classifyBulkOrIntermediateOrderIdentifier(identifier.value);
});
const rows = ref<BulkOrderCreationRow[]>([]);
// Shared by Shipping PO generation and live Suggested Orders Weekly Sales.
const demandHorizon = ref("30");
const suggestedOrdersDemoRows = ref<SuggestedOrdersDemoRow[]>([]);
const liveAssociatedIntermediateOrders = ref<AssociatedIntermediateOrder[]>([]);
const isLoading = ref(false);
const errorMessage = ref("");
// View-only search review state when the live search finds no Intermediate PO rows.
const bulkAllocations = ref<BulkOrderAllocationRow[]>([]);
const searchDiagnostics = ref<IntermediateOrderSearchDiagnostics>();
const hasSearchReview = computed(
  () =>
    !isDemoMode.value &&
    (bulkAllocations.value.length > 0 || searchDiagnostics.value !== undefined)
);
const hasSearched = ref(false);
const lookupStatus = ref("Enter an identifier to begin.");
const lookupSucceeded = ref(false);
const generationWorkspace = ref<ShippingPoWorkspaceControls>();
const selectedIntermediateOrderId = ref<string>();
const committedResultContext = ref<{
  identifier: string;
  mode: "bulk-order" | "intermediate-order";
}>();
const bulkOrderResultContext = ref<{
  identifier: string;
  rows: BulkOrderCreationRow[];
  associatedIntermediateOrders: AssociatedIntermediateOrder[];
}>();
const demoQuantityRows = computed(() =>
  buildSuggestedOrdersDemoShippingRows(suggestedOrdersDemoRows.value)
);
const generationRows = computed(() => {
  if (!isDemoMode.value) return rows.value;
  return selectedIntermediateOrderId.value
    ? demoRowsForIntermediateOrder(
        demoQuantityRows.value,
        selectedIntermediateOrderId.value
      )
    : demoQuantityRows.value;
});
const associatedIntermediateOrders = computed(() =>
  isDemoMode.value &&
  rows.value.length > 0 &&
  isDemoAlgorithmIdentifier(generationIdentifier.value)
    ? demoAssociatedOrders(demoQuantityRows.value)
    : liveAssociatedIntermediateOrders.value
);
const bulkOrderResultSummary = computed(() => {
  const context = bulkOrderResultContext.value;
  if (!context) return;
  const unitTotal = context.rows.reduce(
    (sum, row) => sum + row.requestedUnits,
    0
  );
  const warehouseSummary = [
    ...new Set(
      context.associatedIntermediateOrders.map((order) => order.warehouse)
    ),
  ].join(", ");
  return {
    identifier: context.identifier,
    unitTotal,
    warehouseSummary,
  };
});
let lookupGeneration = 0;
let modeGeneration = 0;

function shouldRetainBulkOrderContextForLookup(
  lookupMode: "bulk-order" | "intermediate-order" | null,
  searchedIdentifier: string
): boolean {
  if (lookupMode !== "intermediate-order" || !bulkOrderResultContext.value) {
    return false;
  }
  return bulkOrderResultContext.value.associatedIntermediateOrders.some(
    (order) => order.id === searchedIdentifier
  );
}

watch(
  isDemoMode,
  async (demoModeEnabled) => {
    modeGeneration += 1;
    lookupGeneration += 1;
    isLoading.value = false;
    associatedIntermediateOrderIdentifier.value = "";
    errorMessage.value = "";
    bulkAllocations.value = [];
    searchDiagnostics.value = undefined;
    selectedIntermediateOrderId.value = undefined;
    committedResultContext.value = undefined;
    bulkOrderResultContext.value = undefined;

    if (demoModeEnabled) {
      identifier.value = "Z-77204";
      generationIdentifier.value = identifier.value;
      suggestedOrdersDemoRows.value = createAlgorithmDemoRows();
      const result = lookupBulkOrderCreation({
        mode: "bulk-order",
        identifier: identifier.value,
      });
      rows.value = demoQuantityRows.value;
      liveAssociatedIntermediateOrders.value =
        result.associatedIntermediateOrders;
      hasSearched.value = true;
      lookupStatus.value = "Lookup complete.";
      lookupSucceeded.value = true;
      committedResultContext.value = {
        identifier: identifier.value.trim().toUpperCase(),
        mode: "bulk-order",
      };
      warehouses.value = DEMO_ALGORITHM_WAREHOUSES;
      return;
    }

    identifier.value = "";
    generationIdentifier.value = "";
    suggestedOrdersDemoRows.value = [];
    rows.value = [];
    liveAssociatedIntermediateOrders.value = [];
    errorMessage.value = "";
    hasSearched.value = false;
    lookupStatus.value = "Enter an identifier to begin.";
    lookupSucceeded.value = false;

    try {
      const liveWarehouses = await fetchWarehouses();
      if (!isDemoMode.value) warehouses.value = liveWarehouses;
    } catch {
      if (!isDemoMode.value) warehouses.value = [];
    }
  },
  { immediate: true }
);

const lookupFieldLabel = "Bulk Order Search";

const resultLabel = computed(() => {
  if (rows.value.length === 0 && bulkAllocations.value.length > 0) {
    const count = bulkAllocations.value.length;
    return `${String(count)} allocation line${count === 1 ? "" : "s"} (view only)`;
  }
  return rows.value.length === 1
    ? "1 SKU found"
    : `${String(rows.value.length)} SKUs found`;
});
const suggestedOrdersRowCount = computed(() =>
  isDemoMode.value ? SUGGESTED_ORDERS_DEMO_ROW_COUNT : rows.value.length
);
const sourceLabel = computed(() =>
  isDemoMode.value
    ? "Randomized sample based on the IFR Orders and Details exports."
    : committedResultContext.value
      ? `Returned from ${
          committedResultContext.value.mode === "bulk-order"
            ? "Bulk Order"
            : "Intermediate Order"
        } ${committedResultContext.value.identifier}`
      : "Enter a Bulk Order or Intermediate Order identifier."
);
const resultsTitle = computed(() => {
  const skuCount = suggestedOrdersRowCount.value;
  const skuCountLabel = `${String(skuCount)} SKU${skuCount === 1 ? "" : "s"}`;
  const context = committedResultContext.value;
  if (!context || rows.value.length === 0) {
    return `Suggested Orders (${skuCountLabel})`;
  }
  const orderType =
    context.mode === "bulk-order" ? "Bulk Order" : "Intermediate Order";
  return `Suggested Orders | ${orderType} ${context.identifier} - (${skuCountLabel})`;
});
const shippingTitle = computed(() => {
  const context = committedResultContext.value;
  const sourceRows = generationRows.value;
  if (!context || sourceRows.length === 0) return "Calculate Shipping POs";
  const divisions = [
    ...new Set(sourceRows.map((row) => row.division?.trim()).filter(Boolean)),
  ];
  const divisionLabel =
    divisions.length > 0 ? ` | Division - ${divisions.join(", ")}` : "";
  const orderType =
    context.mode === "bulk-order" ? "Bulk Order" : "Intermediate Order";
  const units = sourceRows.reduce((sum, row) => sum + row.requestedUnits, 0);
  return `Calculate Shipping POs${divisionLabel} | ${orderType} ${context.identifier} - (${String(sourceRows.length)} SKU${sourceRows.length === 1 ? "" : "s"}, ${String(units)} unit${units === 1 ? "" : "s"})`;
});
const identifierError = computed(() => {
  if (!hasSearched.value) return "";
  if (lookupSucceeded.value) return "";
  if (!identifier.value.trim()) {
    return "Enter a Bulk Order or Intermediate Order identifier.";
  }
  if (!detectedMode.value) {
    return "Enter a valid Bulk Order or Intermediate Order identifier.";
  }
  return "";
});

watch(
  identifier,
  (value) => {
    lookupSucceeded.value = false;
    if (isLoading.value) {
      lookupGeneration += 1;
      isLoading.value = false;
      lookupStatus.value = "Identifier changed. Look up the order again.";
    }
    if (value.trim() !== associatedIntermediateOrderIdentifier.value) {
      associatedIntermediateOrderIdentifier.value = "";
    }
  },
  { flush: "sync" }
);

async function lookup(): Promise<void> {
  associatedGenerationError.value = "";
  if (isLoading.value) return;

  hasSearched.value = true;
  lookupSucceeded.value = false;
  errorMessage.value = "";
  bulkAllocations.value = [];
  searchDiagnostics.value = undefined;
  selectedIntermediateOrderId.value = undefined;
  if (!identifier.value.trim()) {
    errorMessage.value = "Enter a Bulk Order or Intermediate Order identifier.";
    lookupStatus.value = "Lookup not run: identifier required";
    return;
  }
  const lookupMode = detectedMode.value;
  if (!lookupMode) {
    errorMessage.value =
      "Enter a valid Bulk Order or Intermediate Order identifier.";
    lookupStatus.value = "Lookup not run: invalid identifier";
    return;
  }
  const searchedIdentifier = identifier.value.trim();
  if (!shouldRetainBulkOrderContextForLookup(lookupMode, searchedIdentifier)) {
    bulkOrderResultContext.value = undefined;
  }
  const currentLookupGeneration = ++lookupGeneration;
  isLoading.value = true;
  lookupStatus.value = "Looking up order contents...";

  if (isDemoMode.value) {
    await wait(LOOKUP_SIMULATED_DELAY_MS);
    if (currentLookupGeneration !== lookupGeneration) return;
    if (isMockInvalidIdentifier(identifier.value)) {
      errorMessage.value =
        "No orders found for this search. Check the identifier and try again.";
    } else if (isMockEmptyIdentifier(identifier.value)) {
      errorMessage.value =
        "No orders found for this search. Check the identifier and try again.";
    } else {
      const result = lookupBulkOrderCreation({
        mode: lookupMode,
        identifier: identifier.value,
      });
      selectedIntermediateOrderId.value = resolveDemoIntermediateOrderId(
        identifier.value
      );
      rows.value = selectedIntermediateOrderId.value
        ? demoRowsForIntermediateOrder(
            demoQuantityRows.value,
            selectedIntermediateOrderId.value
          )
        : isDemoAlgorithmIdentifier(identifier.value)
          ? demoQuantityRows.value
          : result.rows;
      liveAssociatedIntermediateOrders.value =
        result.associatedIntermediateOrders;
      if (
        lookupMode === "bulk-order" &&
        result.associatedIntermediateOrders.length > 0
      ) {
        bulkOrderResultContext.value = {
          identifier: searchedIdentifier,
          rows: [...rows.value],
          associatedIntermediateOrders: [
            ...result.associatedIntermediateOrders,
          ],
        };
      }
      if (rows.value.length === 0) {
        errorMessage.value =
          "No orders found for this search. Check the identifier and try again.";
      }
    }
    isLoading.value = false;
    lookupSucceeded.value = !errorMessage.value && rows.value.length > 0;
    if (lookupSucceeded.value) {
      generationIdentifier.value = identifier.value.trim();
      committedResultContext.value = {
        identifier: generationIdentifier.value.toUpperCase(),
        mode: lookupMode,
      };
    }
    lookupStatus.value = errorMessage.value
      ? "Lookup returned no matching orders"
      : "Lookup complete.";
    return;
  }

  let statusOnError = "Lookup returned no matching orders";
  try {
    const searchResult = await searchIntermediateOrderResultByIdentifier(
      identifier.value,
      true
    );
    if (currentLookupGeneration !== lookupGeneration) return;
    const skuRows = searchResult.rows;
    if (skuRows.length === 0) {
      rows.value = [];
      liveAssociatedIntermediateOrders.value = [];
      const diagnostics = searchResult.diagnostics;
      if (
        searchResult.bulkAllocations.length > 0 ||
        (diagnostics && diagnostics.failureStage !== "NotFound")
      ) {
        bulkAllocations.value = searchResult.bulkAllocations;
        searchDiagnostics.value = diagnostics;
      } else {
        errorMessage.value =
          diagnostics?.message ??
          "No orders found for this search. Check the identifier and try again.";
      }
    } else {
      const isIntermediateOrderSearch =
        lookupMode === "intermediate-order" ||
        skuRows.some((row) =>
          matchesIntermediateOrder(row, searchedIdentifier)
        );
      selectedIntermediateOrderId.value = isIntermediateOrderSearch
        ? searchedIdentifier
        : undefined;
      const result = mapIntermediateOrderRowsToLookupResult(
        skuRows,
        warehouses.value,
        isIntermediateOrderSearch ? searchedIdentifier : undefined
      );
      rows.value = result.rows;
      liveAssociatedIntermediateOrders.value =
        result.associatedIntermediateOrders;
      if (
        lookupMode === "bulk-order" &&
        result.associatedIntermediateOrders.length > 0
      ) {
        bulkOrderResultContext.value = {
          identifier: searchedIdentifier,
          rows: [...result.rows],
          associatedIntermediateOrders: [
            ...result.associatedIntermediateOrders,
          ],
        };
      }
      if (result.rows.length === 0) {
        errorMessage.value =
          "No SKUs found for this Intermediate Order. Check the identifier and try again.";
      }
    }
  } catch (error) {
    if (currentLookupGeneration !== lookupGeneration) return;
    errorMessage.value =
      error instanceof Error
        ? error.message
        : "Search failed. Check the identifier and try again.";
    statusOnError = "Lookup failed";
  }
  isLoading.value = false;
  lookupSucceeded.value = !errorMessage.value && rows.value.length > 0;
  if (lookupSucceeded.value) {
    generationIdentifier.value = identifier.value.trim();
    committedResultContext.value = {
      identifier: generationIdentifier.value.toUpperCase(),
      mode: lookupMode,
    };
  }
  lookupStatus.value = errorMessage.value
    ? statusOnError
    : hasSearchReview.value
      ? bulkAllocations.value.length > 0
        ? "Bulk allocations only — Intermediate Orders unavailable"
        : "Intermediate Orders unavailable"
      : "Lookup complete.";
}

function openFullIntermediateOrder(): void {
  modalStore.show(FullIntermediateOrderModal, {
    dialogId: "bulk-order-creation-full-io",
    width: 860,
    height: 480,
    componentProps: {
      identifier: generationIdentifier.value,
      rows: isDemoMode.value ? generationRows.value : rows.value,
    },
  });
}

function viewAssociatedIntermediateOrder(
  order: AssociatedIntermediateOrder
): void {
  associatedIntermediateOrderIdentifier.value = order.id;
  identifier.value = order.id;
  const currentModeGeneration = modeGeneration;
  const pendingLookup = lookup();
  const associatedLookupGeneration = lookupGeneration;
  void pendingLookup.then(async () => {
    if (
      currentModeGeneration !== modeGeneration ||
      associatedLookupGeneration !== lookupGeneration
    )
      return;
    if (!lookupSucceeded.value) {
      associatedGenerationError.value =
        "Suggested Shipping POs are unavailable for this order.";
      return;
    }
    await nextTick();
    if (associatedLookupGeneration !== lookupGeneration) return;
    generationWorkspace.value?.generate();
  });
}

function viewBulkOrderResultContext(): void {
  const context = bulkOrderResultContext.value;
  if (!context) return;
  lookupGeneration += 1;
  isLoading.value = false;
  associatedIntermediateOrderIdentifier.value = "";
  associatedGenerationError.value = "";
  selectedIntermediateOrderId.value = undefined;
  identifier.value = context.identifier;
  generationIdentifier.value = context.identifier;
  committedResultContext.value = {
    identifier: context.identifier.toUpperCase(),
    mode: "bulk-order",
  };
  rows.value = [...context.rows];
  liveAssociatedIntermediateOrders.value = [
    ...context.associatedIntermediateOrders,
  ];
  bulkAllocations.value = [];
  searchDiagnostics.value = undefined;
  errorMessage.value = "";
  lookupSucceeded.value = true;
  hasSearched.value = true;
  lookupStatus.value = "Lookup complete.";
}
</script>

<template>
  <div class="page-main-container bulk-order-creation-page overflow-y-auto">
    <div class="bulk-order-creation-heading">
      <h2>Review order contents</h2>
      <RouterLink
        class="bulk-order-creation-demo-toggle"
        :to="{ query: demoModeToggleQuery }"
      >
        {{
          isDemoMode
            ? "Turn off demo mode"
            : "Try demo mode (sample order data)"
        }}
      </RouterLink>
    </div>

    <div class="bulk-order-creation-workspace">
      <aside class="bulk-order-creation-lookup" aria-labelledby="lookup-title">
        <div class="bulk-order-creation-card-heading"></div>

        <form aria-label="Look Up Order" @submit.prevent="void lookup()">
          <FormField :label="lookupFieldLabel" required>
            <Textbox
              v-model="identifier"
              placeholder="Bulk Order or Intermediate Order Number"
              :label="lookupFieldLabel"
            />
            <p
              v-if="identifierError"
              class="bulk-order-creation-field-error"
              role="alert"
            >
              {{ identifierError }}
            </p>
          </FormField>

          <button
            type="submit"
            class="btn btn-primary bulk-order-creation-lookup-button"
            :disabled="isLoading"
            @click.prevent="void lookup()"
          >
            {{ isLoading ? "Looking up..." : "Look up order" }}
          </button>
        </form>

        <div class="bulk-order-creation-divider" aria-hidden="true"></div>

        <div
          class="bulk-order-creation-status-block"
          role="status"
          aria-live="polite"
        >
          <span class="bulk-order-creation-label">Lookup status</span>
          <p :class="{ 'is-error': Boolean(errorMessage) || hasSearchReview }">
            <span
              class="bulk-order-creation-status-dot"
              aria-hidden="true"
            ></span>
            {{ lookupStatus }}
          </p>
        </div>

        <div class="bulk-order-creation-returned">
          <span class="bulk-order-creation-label">Returned results</span>
          <strong>{{ resultLabel }}</strong>
        </div>

        <div
          v-if="associatedIntermediateOrders.length > 0"
          class="bulk-order-creation-associated-order"
        >
          <small v-if="bulkOrderResultSummary">Bulk Order</small>
          <button
            v-if="bulkOrderResultSummary"
            type="button"
            class="bulk-order-creation-associated-order-row"
            data-testid="bulk-order-result-row"
            @click="viewBulkOrderResultContext"
            @keydown.enter.prevent="viewBulkOrderResultContext"
          >
            <strong>{{ bulkOrderResultSummary.identifier }}</strong>
            <span
              >{{ bulkOrderResultSummary.warehouseSummary }}&nbsp;|&nbsp;{{
                bulkOrderResultSummary.unitTotal.toLocaleString()
              }}
              units</span
            >
          </button>
          <small>{{
            associatedIntermediateOrders.length === 1
              ? "Associated Intermediate Order"
              : "Associated Intermediate Orders"
          }}</small>
          <button
            v-for="associatedOrder in associatedIntermediateOrders"
            :key="associatedOrder.id"
            type="button"
            class="bulk-order-creation-associated-order-row"
            data-testid="associated-order-row"
            @click="viewAssociatedIntermediateOrder(associatedOrder)"
            @keydown.enter.prevent="
              viewAssociatedIntermediateOrder(associatedOrder)
            "
          >
            <strong>{{ associatedOrder.id }}</strong>
            <span
              >{{ associatedOrder.warehouse }}&nbsp;|&nbsp;{{
                associatedOrder.unitTotal.toLocaleString()
              }}
              units</span
            >
          </button>
        </div>
      </aside>

      <div class="bulk-order-creation-main-column">
        <section
          class="bulk-order-creation-results"
          :class="{
            'bulk-order-creation-results-review': hasSearchReview && !isLoading,
          }"
          aria-labelledby="results-title"
        >
          <div class="bulk-order-creation-results-header">
            <div>
              <h3 id="results-title">
                {{ resultsTitle }}
                <AppPopover
                  v-if="lookupSucceeded && rows.length > 0 && !isLoading"
                  title="Suggested Orders details"
                  placement="bottom"
                  :width="420"
                  :trigger="['click', 'focus']"
                  tooltip
                >
                  <template #trigger="{ tooltipId }">
                    <button
                      type="button"
                      class="bulk-order-creation-icon-button"
                      aria-label="Suggested Orders details"
                      :aria-describedby="tooltipId"
                    >
                      <LucideIcon name="circle-help" :size="16" />
                    </button>
                  </template>
                  <p>{{ sourceLabel }}</p>
                  <p v-if="!isDemoMode">
                    Live lookup results ({{ rows.length }} SKU lines).
                    Unavailable data is shown as —; no demo values are used.
                    Order and AAO edits stay in this table only; Shipping PO
                    generation uses the source order quantities.
                  </p>
                  <template v-else>
                    <p>
                      Randomized demo values from the Orders and Details exports
                      ({{ SUGGESTED_ORDERS_DEMO_ROW_COUNT }} SKUs).
                    </p>
                    <p>
                      Order and AAO are editable; AAO stays at or above
                      Available and AAOR is calculated from the Primary SKU.
                    </p>
                    <p v-if="selectedIntermediateOrderId" role="note">
                      The demo table edits bulk quantities. Generation for
                      {{ selectedIntermediateOrderId }}
                      uses its 60% / 30% / remainder allocation share.
                    </p>
                  </template>
                </AppPopover>
              </h3>
            </div>
            <div
              v-if="rows.length > 0"
              class="bulk-order-creation-results-badges"
            >
              <span class="bulk-order-creation-count-badge">
                {{ suggestedOrdersRowCount }} SKU line{{
                  suggestedOrdersRowCount === 1 ? "" : "s"
                }}
              </span>
              <span class="bulk-order-creation-count-badge">
                {{ associatedIntermediateOrders.length }} associated IO{{
                  associatedIntermediateOrders.length === 1 ? "" : "s"
                }}
              </span>
              <span
                v-if="!errorMessage"
                class="bulk-order-creation-success-badge"
              >
                Lookup success
              </span>
              <AppPopover
                placement="bottom"
                :trigger="['hover', 'focus']"
                tooltip
              >
                <template #trigger="{ tooltipId }">
                  <button
                    ref="fullOrderTrigger"
                    type="button"
                    class="bulk-order-creation-icon-button"
                    aria-label="Open full Intermediate Order dialog"
                    aria-haspopup="dialog"
                    :aria-describedby="tooltipId"
                    @click="openFullIntermediateOrder"
                  >
                    <LucideIcon name="external-link" :size="16" />
                  </button>
                </template>
                Open full Intermediate Order dialog
              </AppPopover>
            </div>
          </div>

          <StatusBanner v-if="errorMessage" variant="error" role="alert">
            {{ errorMessage }}
          </StatusBanner>

          <BulkOrderAllocationReview
            v-if="hasSearchReview && !isLoading"
            :diagnostics="searchDiagnostics"
            :allocations="bulkAllocations"
          />

          <div
            v-if="isLoading"
            class="bulk-order-creation-loading"
            role="status"
          >
            Looking up order contents...
          </div>

          <template v-if="rows.length > 0">
            <div class="bulk-order-creation-table-wrap">
              <SuggestedOrdersDemo
                v-if="isDemoMode"
                :rows="suggestedOrdersDemoRows"
                @update:rows="suggestedOrdersDemoRows = $event"
              />
              <SuggestedOrdersDemo
                v-else
                :live-rows="rows"
                :rate-of-sale-days="Number(demandHorizon)"
              />
            </div>
          </template>

          <div
            v-else-if="!isLoading && !hasSearchReview"
            class="bulk-order-creation-empty-state"
          >
            Enter an identifier to see returned SKU lines.
          </div>
        </section>

        <section
          class="bulk-order-creation-shipping"
          aria-labelledby="shipping-pos-title"
        >
          <ShippingPoGenerationWorkspace
            ref="generationWorkspace"
            v-model:horizon="demandHorizon"
            :identifier="generationIdentifier"
            :intermediate-order-id="selectedIntermediateOrderId"
            :rows="generationRows"
            :warehouse-options="warehouses"
            :demo-mode="isDemoMode"
            :demo-quantity-rows="demoQuantityRows"
            :lookup-loading="isLoading"
            :lookup-valid="lookupSucceeded"
          >
            <template #header>
              <div class="min-w-0">
                <h3 id="shipping-pos-title">{{ shippingTitle }}</h3>
              </div>
            </template>
          </ShippingPoGenerationWorkspace>
          <StatusBanner
            v-if="associatedGenerationError"
            variant="error"
            role="alert"
          >
            {{ associatedGenerationError }}
          </StatusBanner>
        </section>
      </div>
    </div>
  </div>
</template>

<style scoped>
.bulk-order-creation-page {
  min-height: 0;
  overflow: hidden auto;
  gap: 0.75rem;
  padding-bottom: 1rem;
}

.bulk-order-creation-heading h2 {
  margin: 0;
  color: var(--text-primary, #172033);
  font-size: 1.35rem;
}

.bulk-order-creation-heading p,
.bulk-order-creation-card-heading p,
.bulk-order-creation-results-header p {
  margin: 0.35rem 0 0;
  color: var(--text-secondary, #5d6878);
  font-size: 0.85rem;
}

.bulk-order-creation-demo-toggle {
  display: inline-block;
  margin-top: 0.5rem;
  color: var(--action-primary, #1261a0);
  font-size: 0.8rem;
  font-weight: 600;
}

.bulk-order-creation-workspace {
  display: grid;
  flex: 0 0 auto;
  min-height: 18rem;
  grid-template-columns: minmax(240px, 285px) minmax(0, 1fr);
  gap: 1rem;
}

.bulk-order-creation-main-column {
  display: flex;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
  gap: 0.75rem;
  overflow: visible;
}

.bulk-order-creation-lookup,
.bulk-order-creation-results {
  min-width: 0;
  min-height: 0;
  border: 1px solid var(--border-subtle, #d8dee8);
  border-radius: 10px;
  background: var(--surface-primary, #fff);
  box-shadow: 0 8px 24px rgb(20 33 61 / 6%);
  padding: 1.25rem;
}

.bulk-order-creation-lookup {
  align-self: stretch;
  overflow-y: auto;
}

.bulk-order-creation-results {
  display: flex;
  flex: 0 0 360px;
  flex-direction: column;
  height: 360px;
  overflow: hidden;
}

/* View-only allocation review renders its own height-capped grids. */
.bulk-order-creation-results.bulk-order-creation-results-review {
  flex: 0 0 auto;
  height: auto;
  overflow: visible;
}

.bulk-order-creation-card-heading h3,
.bulk-order-creation-results-header h3 {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
  margin: 0;
  color: var(--text-primary, #172033);
  font-size: 1rem;
}

.bulk-order-creation-lookup > .form-field-container {
  margin-top: 1.1rem;
}

.bulk-order-creation-label {
  display: block;
  color: var(--text-secondary, #64748b);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.bulk-order-creation-lookup-button {
  width: 100%;
  min-height: 42px;
  margin-top: 1.1rem;
  white-space: nowrap;
}

.bulk-order-creation-field-error {
  margin: 0.35rem 0 0;
  color: #b60007;
  font-size: 0.78rem;
}

.bulk-order-creation-results-header {
  display: flex;
  flex: 0 0 auto;
  align-items: start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.85rem;
  flex-wrap: wrap;
}

.bulk-order-creation-icon-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: var(--action-primary, #1261a0);
  cursor: pointer;
}

.bulk-order-creation-icon-button:focus-visible {
  outline: 2px solid var(--action-primary, #1261a0);
  outline-offset: 2px;
}

.bulk-order-creation-results-badges {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-left: auto;
  max-width: 100%;
}

.bulk-order-creation-success-badge,
.bulk-order-creation-count-badge {
  flex: 0 0 auto;
  border-radius: 999px;
  padding: 0.45rem 0.75rem;
  font-size: 0.7rem;
  font-weight: 700;
  white-space: nowrap;
}

.bulk-order-creation-success-badge {
  background: #d8f5e4;
  color: #166534;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.bulk-order-creation-count-badge {
  background: #eef4fb;
  color: var(--action-primary, #1261a0);
  letter-spacing: 0.03em;
}

.bulk-order-creation-divider {
  margin: 1.25rem 0;
  border-top: 1px solid var(--border-subtle, #d8dee8);
}

.bulk-order-creation-status-block p,
.bulk-order-creation-returned {
  margin: 0.5rem 0 0;
  color: var(--text-primary, #172033);
  font-size: 0.82rem;
}

.bulk-order-creation-status-block p {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 700;
}

.bulk-order-creation-status-block p.is-error {
  color: #b60007;
}

.bulk-order-creation-status-dot {
  width: 0.6rem;
  height: 0.6rem;
  border-radius: 50%;
  background: #27ae60;
}

.bulk-order-creation-status-block p.is-error .bulk-order-creation-status-dot {
  background: #b60007;
}

.bulk-order-creation-returned {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
}

.bulk-order-creation-returned strong {
  color: var(--action-primary, #1261a0);
}

.bulk-order-creation-associated-order {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-top: 0.75rem;
  border: 2px solid #7cb4ff;
  border-radius: 8px;
  background: #eef4fb;
  padding: 0.85rem 1rem;
  color: var(--text-secondary, #5d6878);
  font-size: 0.78rem;
}

.bulk-order-creation-associated-order strong {
  color: var(--action-primary, #1261a0);
  font-size: 0.95rem;
}

.bulk-order-creation-associated-order small {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.bulk-order-creation-associated-order-row {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  width: 100%;
  border: none;
  background: transparent;
  padding: 0;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.bulk-order-creation-associated-order-row:hover,
.bulk-order-creation-associated-order-row:focus-visible {
  text-decoration: underline;
}

.bulk-order-creation-associated-order-row:focus-visible {
  outline: 2px solid var(--action-primary, #1261a0);
  outline-offset: 2px;
}

.bulk-order-creation-associated-order-row
  + .bulk-order-creation-associated-order-row {
  margin-top: 0.5rem;
  border-top: 1px solid #d3e3f7;
  padding-top: 0.5rem;
}

.bulk-order-creation-table-wrap {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  border: 1px solid var(--border-subtle, #d8dee8);
  border-radius: 8px;
}

.bulk-order-creation-shipping {
  display: flex;
  min-width: 0;
  min-height: 0;
  flex: 0 0 auto;
  flex-direction: column;
  gap: 0.5rem;
  overflow: visible;
  border: 1px solid var(--border-subtle, #d8dee8);
  border-radius: 10px;
  background: var(--surface-primary, #fff);
  padding: 0.75rem;
}

.bulk-order-creation-loading {
  border-radius: 7px;
  background: #eef4fb;
  padding: 0.75rem 1rem;
  color: var(--action-primary, #1261a0);
  font-size: 0.85rem;
  font-weight: 700;
}

.bulk-order-creation-empty-state {
  display: grid;
  min-height: 170px;
  place-items: center;
  border: 1px dashed var(--border-subtle, #d8dee8);
  color: var(--text-secondary, #5d6878);
  text-align: center;
}

.bulk-order-modal {
  display: flex;
  height: 100%;
  flex-direction: column;
}

.bulk-order-modal-body {
  overflow: auto;
}

.bulk-order-modal-summary {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  color: var(--text-secondary, #5d6878);
}

.bulk-order-modal-summary strong {
  color: var(--text-primary, #172033);
}

@media (width <= 850px) {
  .bulk-order-creation-page {
    overflow: hidden auto;
  }

  .bulk-order-creation-workspace {
    min-height: 0;
    flex: 0 0 auto;
    grid-template-columns: 1fr;
  }

  .bulk-order-creation-lookup {
    align-self: stretch;
  }

  .bulk-order-creation-results {
    flex: 0 0 auto;
    height: 400px;
    min-height: 400px;
  }

  .bulk-order-creation-shipping {
    min-height: 22rem;
    flex: 0 0 auto;
  }
}

@media (width <= 560px) {
  .bulk-order-creation-lookup,
  .bulk-order-creation-results {
    padding: 1rem;
  }

  .bulk-order-creation-actions {
    flex-direction: column;
  }

  .bulk-order-creation-results-header {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
